// ===========================================
// Refunds Due Card (#642, admin dashboard)
// ===========================================
// Every open Refund due of the school until it is marked Refunded. Shown AND queried only with
// operations_refunds.write (the owner always has it): `enabled: can(…)`, #272.

import { useState } from 'react';
import { Link as RouterLink, useParams } from 'react-router-dom';
import { Alert, Box, Button, Card, CardContent, CircularProgress, Link, Stack, Typography } from '@mui/material';
import { usePermissions } from '../../hooks/usePermissions';
import { useRefundsDue } from '../../hooks/useFamilyCredit';
import type { RefundDue } from '../../api/familyCredit.api';
import { formatCurrencyFromDollars } from '../../utils/currency';
import MarkRefundedDialog from '../admin/families/MarkRefundedDialog';

/** A @db.Date key (YYYY-MM-DD) as dd/mm/yyyy, without a Date object. */
const dayKeyToAu = (key: string) => key.split('-').reverse().join('/');

export default function RefundsDueCard() {
  const { can } = usePermissions();
  const mayRefund = can('operations_refunds', 'write');
  const { schoolSlug } = useParams<{ schoolSlug: string }>();
  const { data, isLoading, isError } = useRefundsDue(mayRefund);
  const [toRefund, setToRefund] = useState<RefundDue | null>(null);

  if (!mayRefund) return null;
  // Nothing to do: the card stays out of the way.
  if (!isLoading && !isError && (data ?? []).length === 0) return null;

  return (
    <Card data-testid="refunds-due-card">
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Refunds due
        </Typography>
        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
            <CircularProgress size={24} />
          </Box>
        ) : isError ? (
          <Alert severity="error">Could not load the refunds due.</Alert>
        ) : (
          <Stack spacing={1.5}>
            {(data ?? []).map((r) => (
              <Box key={r.id} sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, alignItems: 'center' }}>
                <Box sx={{ minWidth: 0 }}>
                  {r.parentId ? (
                    <Link component={RouterLink} to={`/${schoolSlug}/admin/families?parent=${encodeURIComponent(r.parentId)}`}>
                      {r.familyName}
                    </Link>
                  ) : (
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {r.familyName}
                    </Typography>
                  )}
                  <Typography variant="body2">
                    {formatCurrencyFromDollars(parseFloat(r.amount))} · flagged {dayKeyToAu(r.occurredOn)}
                  </Typography>
                  {r.note && (
                    <Typography variant="caption" color="text.secondary" sx={{ overflowWrap: 'anywhere' }}>
                      {r.note}
                    </Typography>
                  )}
                </Box>
                <Button size="small" variant="outlined" onClick={() => setToRefund(r)} sx={{ flexShrink: 0 }}>
                  Mark refunded
                </Button>
              </Box>
            ))}
          </Stack>
        )}
      </CardContent>
      <MarkRefundedDialog
        open={!!toRefund}
        onClose={() => setToRefund(null)}
        due={toRefund ? { id: toRefund.id, amount: toRefund.amount, familyName: toRefund.familyName } : null}
      />
    </Card>
  );
}
